/*
 * docs: question generation API reference — POST api/question-generator/generate
 *
 * Ortak request alanları:
 *   narrationText  string   ZORUNLU — sorunun üretileceği kaynak metin
 *   questionType   enum     ZORUNLU — multiple_choice | multiple_answer | free_response |
 *                                     true_false | drag_drop | fill_in_blank | matching | ordering
 *   navId          string   Sorunun ekleneceği sahne id'si
 *   trainingId     string   ZORUNLU — eğitim id'si
 *   voiceId        string   Opsiyonel — TTS ses id'si
 *   provider       string   Opsiyonel — azure | gemini | elevenlabs
 *   language       string   Opsiyonel — ör. tr-TR (elevenlabs'ta her zaman "multilingual")
 *
 * Ortak response zarfı (her tip):
 *   {
 *     "navItem":            { id, name, type, background, customSize, sortable_id },
 *     "boxesById":          { ... },   // kutu geometrileri/parent/container
 *     "pluginToolbarsById": { ... },   // her kutunun pluginId + state
 *     "correctAnswers":     { ... },   // rootBoxId → doğru cevap (tipe göre biçim)
 *     "soundList":          {},        // TTS istenmişse soundList[navId] dolar
 *     "narrationAudioUrl":  null,      // TTS istenmişse set edilir
 *     "qualityScore":       number,
 *     "evaluation":         { scores, overallScore, pass, feedback }
 *   }
 *
 * ── 1. TrueFalse (questionType: "true_false") ──────────────────────────────
 * Request:
 *   {
 *     "narrationText": "Ankara Türkiye'nin başkentidir. İstanbul en kalabalık şehirdir ama başkent değildir.",
 *     "questionType": "true_false",
 *     "navId": "pa-abc12345",
 *     "trainingId": "train-001",
 *     "language": "tr-TR",
 *     "voiceId": "tr-TR-EmelNeural",
 *     "provider": "azure"
 *   }
 * Response (rootBoxId = bo-<s>-tf):
 *   - boxesById: sc-Question, sc-Answer0..N, sc-TrueFeedback, sc-FalseFeedback container'ları
 *   - pluginToolbarsById["bo-<s>-tf"].pluginId = "TrueFalse", state: { nBoxes, showFeedback, buttons, quizColor }
 *   - alt kutular pluginId = "BasicText", state.__text = ifade HTML'i
 *   - correctAnswers: { "bo-<s>-tf": [true, false, true] }  // ifadelerle hizalı boolean[]; true = ifade doğru
 *
 * ── 2. DragDrop (questionType: "drag_drop") ────────────────────────────────
 * Request:
 *   {
 *     "narrationText": "Yazılım geliştirme yaşam döngüsü: analiz, tasarım, kodlama, test, bakım.",
 *     "questionType": "drag_drop",
 *     "navId": "pa-abc12345",
 *     "trainingId": "train-001",
 *     "language": "tr-TR"
 *   }
 * Response (rootBoxId = bo-<s>-dd):
 *   - boxesById: sc-Question, sc-Answer0..N (sürüklenebilir choices), sc-TrueFeedback, sc-FalseFeedback
 *   - pluginToolbarsById["bo-<s>-dd"].pluginId = "DragDrop", state: { nBoxes, showFeedback, allowPartialScore, buttons, quizColor }
 *   - alt kutular pluginId = "BasicText", state.__text = choice HTML'i
 *   - correctAnswers: { "bo-<s>-dd": [1, 3, 4, 0, 2] }  // choice indeksleri; boşluklara doğru doldurma sırası
 *     (choices karışık gösterilir; ör. a1=Analiz ilk boşluğa gider)
 *
 * Not — TTS: voiceId/provider gönderilirse soundList[navId] dolar + narrationAudioUrl set edilir;
 *            yoksa soundList {} ve narrationAudioUrl null döner.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import InteractivePane from "../interactive_item/InteractivePane";
import { LuCheck, LuPause, LuPlay, LuHistory, LuEye, LuX } from "react-icons/lu";
import AppModal from "../common/modal/AppModal";
import i18n from "i18next";
import { getToken } from "../../../common/environment_config.es6";
import { approveJob } from "../../../common/server_utils.es6";
import { waitForJob } from "../../../common/tts_job_poll.es6";
import { onJobsSettled, dropSettledFromBuckets } from "../../utils/jobSettled";
import SceneAudioService from "../../services/sceneAudioService";
import { pickSampleLanguage } from "../../utils/sampleVoiceLanguage";
import IdealStudio from "../../../core/editor/main";
import multipleChoicePreview from "../../../images/assessment_previews/MultipleChoice.svg";
import multipleAnswerPreview from "../../../images/assessment_previews/MultipleAnswer.svg";
import freeResponsePreview from "../../../images/assessment_previews/FreeResponse.svg";
import fillInBlankPreview from "../../../images/assessment_previews/FillInBlank.svg";
import matchingPreview from "../../../images/assessment_previews/Matching.svg";
import dragDropPreview from "../../../images/assessment_previews/DragDrop.svg";
import trueFalsePreview from "../../../images/assessment_previews/TrueFalse.svg";
import "./_questionGeneratorModal.scss";
import summarizeQuestion from "./summarizeQuestion";
import { pickContentLocale } from "../../../common/content_language";

const API_SERVICE_MAP = {
  azure: "microsoft",
  elevenlabs: "elevenlabs",
  google: "gemini",
  sestek: "sestek",
};
const SERVICE_NORMALIZE_MAP = {
  microsoft: "azure",
  azure: "azure",
  elevenlabs: "elevenlabs",
  gemini: "google",
  google: "google",
  sestek: "sestek",
};

const SERVICE_LABELS = {
  elevenlabs: "ElevenLabs",
  azure: "Azure",
  microsoft: "Azure",
  google: "Google",
  gemini: "Google",
  sestek: "Sestek",
};

// Ses cinsiyetini normalize eder (AIVoiceTab ile aynı).
const normalizeGender = (g) => {
  const v = String(g ?? "")
    .toLowerCase()
    .trim();
  if (v === "male" || v === "m" || v === "0" || v === "erkek") return "male";
  if (v === "female" || v === "f" || v === "1" || v === "kadın" || v === "kadin") return "female";
  return "";
};

// Cinsiyete göre renk (AIVoiceTab ile aynı): erkek mavi, kadın pembe, bilinmeyen nötr mor.
const getGenderColors = (gender) => {
  const g = normalizeGender(gender);
  if (g === "male") return { bg: "#e0f2fe", color: "#2563eb" };
  if (g === "female") return { bg: "#ffe4f0", color: "#db2777" };
  return { bg: "#ede9fe", color: "#7c3aed" };
};
const genderAvatarStyle = (gender) => {
  const c = getGenderColors(gender);
  return { background: c.bg, color: c.color };
};

// "Tümü" filtresinde seçilen sesin gerçek sağlayıcısını türetir (AIVoiceTab ile aynı).
const voiceToService = (voice) => {
  const raw = String(voice?.service ?? voice?.provider ?? voice?.servicename ?? "").toLowerCase();
  if (raw.includes("eleven")) return "elevenlabs";
  if (raw.includes("google") || raw.includes("gemini")) return "google";
  if (raw.includes("azure") || raw.includes("microsoft")) return "azure";
  if (raw.includes("sestek")) return "sestek";
  return "";
};

// voiceData.provider'ı (sayısal/eski string biçimleri dahil) normalize eder
// (AIVoiceTab loadSavedSound ile aynı eşleme).
const normalizeProvider = (provider, narratorMode) => {
  if (narratorMode === "own") return "private";
  let p = provider;
  if (p === "Microsoft" || p === 0) return "azure";
  if (p === 1) return "elevenlabs";
  if (p === 2 || p === "gemini") return "google";
  if (p === "private" || p === "cloned") return "private";
  return SERVICE_NORMALIZE_MAP[p] || p || "";
};

const normalizeLanguageCode = (lang) => {
  if (!lang) return "";
  const l = lang.toLowerCase();
  if (l.startsWith("tr")) return "tr-TR";
  if (l === "en-gb" || l === "en-uk") return "en-GB";
  if (l.startsWith("en")) return "en-US";
  if (l.startsWith("es")) return "es-ES";
  if (l.startsWith("de")) return "de-DE";
  if (l.startsWith("fr")) return "fr-FR";
  if (l.startsWith("ja")) return "ja-JP";
  if (l.startsWith("zh")) return "zh-CN";
  if (l.startsWith("az")) return "az-AZ";
  if (l.startsWith("pt")) return "pt-BR";
  if (l.startsWith("ar")) return "ar-SA";
  return lang;
};

const LANGUAGE_OPTIONS = {
  google: [
    { value: "ar-SA", label: "Arabic" },
    { value: "de-DE", label: "German" },
    { value: "en-US", label: "English (US)" },
    { value: "es-ES", label: "Spanish" },
    { value: "fr-FR", label: "French" },
    { value: "ja-JP", label: "Japanese" },
    { value: "tr-TR", label: "Turkish" },
    { value: "az-AZ", label: "Azerbaijani" },
  ],
  elevenlabs: [{ value: "multilingual", label: "Multilingual" }],
  azure: [
    { value: "de-DE", label: "German" },
    { value: "en-GB", label: "English (UK)" },
    { value: "en-US", label: "English (US)" },
    { value: "es-ES", label: "Spanish" },
    { value: "fr-FR", label: "French" },
    { value: "ja-JP", label: "Japanese" },
    { value: "multilingual", label: "Multilingual" },
    { value: "pt-BR", label: "Portuguese (BR)" },
    { value: "tr-TR", label: "Turkish" },
    { value: "az-AZ", label: "Azerbaijani" },
  ],
  sestek: [
    { value: "tr-TR", label: "Turkish" },
    { value: "en-US", label: "English (US)" },
    { value: "de-DE", label: "German" },
  ],
};

// Soru tipi kartları — AssessmentItemsPanel ile aynı tasarım/yapı/adlar:
// SVG preview görseli + plugin adı (i18n <Plugin>.PluginName). Değerler API
// kontratı için sabit; etiketler render anında i18n.t ile çözülür.
const QUESTION_TYPE_DEFS = [
  {
    value: "multiple_choice",
    preview: multipleChoicePreview,
    nameKey: "MultipleChoice.PluginName",
    fallback: "Çoktan Seçmeli",
  },
  {
    value: "multiple_answer",
    preview: multipleAnswerPreview,
    nameKey: "MultipleAnswer.PluginName",
    fallback: "Çok Cevaplı",
  },
  {
    value: "free_response",
    preview: freeResponsePreview,
    nameKey: "FreeResponse.PluginName",
    fallback: "Açık Uçlu",
  },
  {
    value: "true_false",
    preview: trueFalsePreview,
    nameKey: "TrueFalse.PluginName",
    fallback: "Doğru/Yanlış",
  },
  {
    value: "drag_drop",
    preview: dragDropPreview,
    nameKey: "DragDrop.PluginName",
    fallback: "Sürükle Bırak",
  },
  {
    value: "fill_in_blank",
    preview: fillInBlankPreview,
    nameKey: "FillInBlank.PluginName",
    fallback: "Boşluk Doldurma",
  },
  {
    value: "matching",
    preview: matchingPreview,
    nameKey: "Matching.PluginName",
    fallback: "Eşleştirme",
  },
  {
    value: "ordering",
    icon: "format_list_numbered",
    nameKey: "QuestionGenerator.Ordering",
    fallback: "Sıralama",
  },
];

// soundList map'inden bir sahnenin anlatım metnini çıkarır (narrationTextOriginal
// öncelikli — kullanıcının orijinali; yoksa narrationText).
const sceneNarrationFrom = (soundListAll, id) => {
  const entry = soundListAll?.[id];
  const first = Array.isArray(entry) ? entry[0] : null;
  return first?.narrationTextOriginal || first?.narrationText || "";
};

// "Çoklu Anlatımdan" tab'ının sanal sahne anahtarı. countsBySceneId /
// resultsBySceneId / generatingScenes vb. tüm per-sahne state bu anahtarla
// yeniden kullanılır (ayrı bir akış yazılmaz).
const COMBINED_KEY = "__combined__";

const QuestionGeneratorModal = ({
  show,
  onClose,
  onCancel,
  trainingId,
  navId,
  onQuestionGenerated,
  voiceProfile,
  soundList,
  navItemsIds = [],
  navItemsById = {},
  soundListAll = {},
  pluginToolbarsById = {},
}) => {
  // Soru (evaluation) sahnesi mi? Sayfada category:"evaluation" plugin'li kutu
  // varsa evet — bu sahnelerden soru ÜRETİLMEZ, listede gösterilmez.
  //
  // InteractiveItem BUNUN DIŞINDA, kategorisi "evaluation" olmasına rağmen.
  // Kuralın amacı, içeriği zaten bir sınav olan sahneyi kaynak olarak
  // kullanmamak; oysa interaktif öğe sahnenin TAMAMI değil, anlatımlı bir
  // sahnenin üzerindeki bir eleman. Dışarıda bırakılmasaydı, bir sahneye
  // çevrilen kart eklemek o sahneyi Soru Üretici'nin listesinden tamamen
  // kaldırırdı — ve kendi sekmesinden eklenen bir şey, kendi listesini
  // budardı.
  const isQuestionScene = (id) => {
    const boxes = navItemsById?.[id]?.boxes || [];
    return boxes.some((boxId) => {
      const pid = pluginToolbarsById?.[boxId]?.pluginId;
      if (!pid || pid === "InteractiveItem") return false;
      const cfg = IdealStudio?.Plugins?.get?.(pid)?.getConfig?.();
      return cfg?.category === "evaluation";
    });
  };

  const [sceneAudio, setSceneAudio] = useState(null);
  // Sahne başına ÜRETİLECEK tip/adet seçimi: { [sceneId]: { multiple_choice: 2, ... } }.
  const [countsBySceneId, setCountsBySceneId] = useState({});
  // O an ARKA PLANDA üretilen sahneler: { [sceneId]: true }. Çoklu eşzamanlı job —
  // bir sahne üretilirken başka sahneye geçip orada da üretim başlatılabilir.
  const [generatingScenes, setGeneratingScenes] = useState({});
  // Sahne başına ÜRETİLMİŞ, ONAY BEKLEYEN sorular: { [sceneId]: [{ q, voice }] }.
  // Sahneye henüz YAZILMAZ; o sahnenin view'ında liste + "Onayla" ile eklenir.
  // Aynı sahnede tekrar üretim üstüne EKLER (append).
  const [resultsBySceneId, setResultsBySceneId] = useState({});
  // Review'da SEÇİMİ KALDIRILMIŞ (tik'i kapatılmış) soru indeksleri: { [sceneId]: [i,..] }.
  // Varsayılan boş → hepsi seçili gelir. Onayla yalnız seçili (tik'li) olanları ekler.
  const [deselectedBySceneId, setDeselectedBySceneId] = useState({});
  // Soru-batch job'u iş tepsisinden onaylanır/reddedilirse (JobWidgetHost →
  // onQuestionBatchApprove soruları zaten ekler) bu modalın bekleyen listesi
  // bayatlar; "Onayla" aynı soruları ikinci kez eklerdi. Modal kapanınca da
  // mount kaldığı için (EditorModals) listener her zaman aktif.
  const resultsBySceneIdRef = useRef(resultsBySceneId);
  resultsBySceneIdRef.current = resultsBySceneId;
  useEffect(
    () =>
      onJobsSettled((jobIds) => {
        const { next, changedKeys } = dropSettledFromBuckets(resultsBySceneIdRef.current, jobIds);
        if (changedKeys.length) {
          resultsBySceneIdRef.current = next;
          setResultsBySceneId(next);
          // Index'li seçim düşen girdilerle kayar → etkilenen sahnelerde sıfırla.
          setDeselectedBySceneId((prev) => {
            const sel = { ...prev };
            changedKeys.forEach((k) => delete sel[k]);
            return sel;
          });
        }
      }),
    []
  );
  // Önizleme overlay'inde gösterilen soru payload'ı (göz ikonuyla açılır) | null.
  const [previewQuestion, setPreviewQuestion] = useState(null);
  // Sahne başına üretim hatası — o sahnenin view'ında gösterilir.
  const [errorBySceneId, setErrorBySceneId] = useState({});
  // Anlatım metinleri arasında gezinme: o an gösterilen sahnenin scenesList indeksi.
  const [viewIndex, setViewIndex] = useState(0);
  // Sahne başına düzenlenebilir narration taslağı (id → metin). Sahneye YAZILMAZ;
  // sadece bu modalda generate için kullanılır. undefined = kullanıcı düzenlemedi.
  const [draftsById, setDraftsById] = useState({});

  // Aktif tab: "single" = tek sahneden (mevcut davranış), "combined" = çoklu
  // anlatımdan tek/çok soru üret.
  const [activeTab, setActiveTab] = useState("single");
  // "combined" tab'da soru üretimine DAHİL edilen sahne id'leri (sol listedeki
  // toggle'lar). Bunların narration'ları birleştirilir.
  const [includedIds, setIncludedIds] = useState([]);
  // "combined" tab'ın ayrı narration taslakları (id → metin). draftsById'den
  // AYRI → tablar birbirini bozmaz, orijinal sahne değişmez.
  const [combinedDraftsById, setCombinedDraftsById] = useState({});
  // Tek sahne tab'da bir kez varsayılan seed edilmiş sahne id'leri. Kullanıcının
  // sonradan 0'a düşürmesi/değiştirmesi korunsun diye reseed etmez.
  const seededScenesRef = useRef(new Set());

  // Soru geçmişi — açılır panel (üstte). LAZY: modal açılışında otomatik fetch YOK;
  // panel ilk açılınca (veya farklı training) bir kez çekilir, trainingId'ye cache.
  const [historyItems, setHistoryItems] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoadedFor, setHistoryLoadedFor] = useState(null);
  // Bu oturumda geçmişten eklenen soru id'leri (item.id = "<jobId>:<i>"). navItemsIds
  // prop'u tazelenene kadarki pencerede çift eklemeyi engeller.
  const [addedHistoryIds, setAddedHistoryIds] = useState([]);
  // Modal her açıldığında seçimi/adetleri/taslakları sıfırla; mevcut sahne açık
  // gelir ve varsayılan 1 çoktan-tek soru ile başlar.
  useEffect(() => {
    // Üretim sürerken, biriken sonuç veya onay ekranı varken formu sıfırlama —
    // tekrar açılışta bunlar korunmalı.
    if (
      !show ||
      Object.keys(generatingScenes).length > 0 ||
      Object.keys(resultsBySceneId).length > 0
    )
      return;
    setCountsBySceneId(navId ? { [navId]: { multiple_choice: 0 } } : {});
    seededScenesRef.current = new Set(navId ? [navId] : []);
    setDraftsById({});
    setActiveTab("single");
    setIncludedIds(navId ? [navId] : []);
    setCombinedDraftsById({});
    // viewIndex, soru sahneleri filtrelenmiş görünür listeye göre.
    const visibleIds = (navItemsIds || []).filter((id) => !isQuestionScene(id));
    const idx = visibleIds.indexOf(navId);
    setViewIndex(idx >= 0 ? idx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, navId]);

  // Kaynak sahneler: soru (evaluation) sahneleri HARİÇ. Numara GERÇEK pozisyon
  // (soru sahnesinin numarası atlanır; ör. 1,2,4). Önce orijinal index'le map,
  // sonra filtrele. Narration'ı olmayanlar listede disabled görünür.
  const scenesList = (navItemsIds || [])
    .map((id, i) => {
      const meta = navItemsById?.[id] || {};
      const narration = sceneNarrationFrom(soundListAll, id);
      return {
        id,
        index: i + 1,
        name: meta.name || `${i18n.t("Scene") || "Sahne"} ${i + 1}`,
        type: meta.type,
        narration,
        hasNarration: !!(narration && narration.trim()),
      };
    })
    .filter((s) => !isQuestionScene(s.id));

  // Bir sahnenin tip→adet haritası ve toplam soru adedi.
  const sceneCounts = (id) => countsBySceneId[id] || {};
  // Sahnede SEÇİLİ (henüz üretilmemiş) toplam adet.
  const sceneTotal = (id) =>
    Object.values(sceneCounts(id)).reduce((a, b) => a + (Number(b) || 0), 0);
  // Sahne için onay bekleyen (üretilmiş) soru sayısı.
  const generatedCount = (id) => (resultsBySceneId[id] || []).length;
  // Sahne için onaylanmış (sahneye eklenmiş) soru sayısı. KALICI: bu oturumda
  // Bu sahneden üretilmiş VE hâlâ GERÇEKTEN mevcut soru sayısı. Geçmişteki
  // (h.navId === id) soru sahnelerinden, üretilen sahnenin (navItem.id)
  // eğitimde durması YETMEZ — içindeki soru objesi (evaluation box) de
  // durmalı. Kullanıcı sahneyi silmeyip yalnız soru objesini silerse
  // isQuestionScene false olur → sayı düşer. (Geçmiş modal açılışında fetch edilir.)
  const committedCount = (id) =>
    historyItems.filter(
      (h) =>
        h.navId === id &&
        h.payload?.navItem?.id &&
        (navItemsIds || []).includes(h.payload.navItem.id) &&
        isQuestionScene(h.payload.navItem.id)
    ).length;

  // Bir sahnenin orijinal narration'ı. Mevcut sahne (navId) için API'den gelen
  // sceneAudio metni önceliklidir; diğer sahneler soundListAll'dan.
  const sceneOriginalText = (id) =>
    id === navId
      ? sceneAudio?.narrationTextOriginal ||
        sceneAudio?.narrationText ||
        sceneNarrationFrom(soundListAll, id)
      : sceneNarrationFrom(soundListAll, id);

  // Gösterilen (gezilen) sahne ve metni. draftsById override edilmişse onu göster.
  const viewedScene = scenesList[viewIndex] || scenesList[0] || null;
  const viewedId = viewedScene?.id;
  const viewedOriginal = viewedId ? sceneOriginalText(viewedId) : "";
  const viewedDraft =
    viewedId && draftsById[viewedId] !== undefined ? draftsById[viewedId] : viewedOriginal;
  const setDraft = (id, val) => setDraftsById((prev) => ({ ...prev, [id]: val }));

  // Tek sahne tab: gezilen her sahne, ilk açılışında "Çoktan Tek Seçim"
  // (multiple_choice) kartı 0 adetle gelir — navId dışındaki sahnelere de
  // uygulanır. seededScenesRef ile bir kez seed edilir; sonraki düzenlemeler
  // korunur.
  useEffect(() => {
    if (!show || activeTab === "combined" || !viewedId) return;
    if (seededScenesRef.current.has(viewedId)) return;
    seededScenesRef.current.add(viewedId);
    setCountsBySceneId((prev) =>
      prev[viewedId] !== undefined ? prev : { ...prev, [viewedId]: { multiple_choice: 0 } }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, activeTab, viewedId]);

  // "combined" tab: bir sahnenin birleşik-taslak metni (düzenlenmemişse orijinal).
  const combinedText = (id) =>
    combinedDraftsById[id] !== undefined ? combinedDraftsById[id] : sceneOriginalText(id);
  const setCombinedDraft = (id, val) => setCombinedDraftsById((prev) => ({ ...prev, [id]: val }));
  // Sahneyi çoklu-üretime dahil et/çıkar (narration'ı olmayan sahne dahil edilemez).
  const toggleInclude = (id) =>
    setIncludedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      // Tıklama sırası değil, sahne sırası: hangi sırada seçilirse seçilsin
      // includedIds hep scenesList sırasında tutulur (gösterim + request buradan gelir).
      return scenesList.map((s) => s.id).filter((sid) => next.includes(sid));
    });

  // İnteraktif sekmesi açıldığında seçim boşsa görüntülenen sahneyle başlar.
  // Böylece bugünkü davranış — "baktığın sahnenin anlatımından üret" — tek bir
  // tıklama gerektirmeden korunuyor; sol listeden ekleme tamamen isteğe bağlı
  // kalıyor.
  useEffect(() => {
    if (activeTab !== "interactive" || !viewedId) return;
    setIncludedIds((prev) => (prev.length ? prev : [viewedId]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, viewedId]);

  // Gezilen sahnenin ses dosyası. Mevcut sahne (navId) için fetch'lenen
  // sceneAudioUrl önceliklidir; diğer sahneler soundListAll'daki ilk dosyadan.
  // (Eskiden sadece navId'nin sesi vardı → play hep ilk/mevcut sahneyi çalıyordu.)
  const soundFileFrom = (id) => {
    const arr = Array.isArray(soundListAll?.[id]) ? soundListAll[id] : null;
    return arr?.find((s) => s?.file)?.file || null;
  };
  const viewedAudioUrl =
    viewedId === navId ? sceneAudioUrl || soundFileFrom(viewedId) : soundFileFrom(viewedId);

  // Voice profile defaults
  const profileService = SERVICE_NORMALIZE_MAP[voiceProfile?.voiceServiceProvider] || "";
  const profileLanguage = voiceProfile?.voiceLanguage || "";
  const profileVoiceName = voiceProfile?.voiceDetail?.name || voiceProfile?.voiceName || "";

  // AIVoiceTab ile aynı default anlatıcı: kayıtlı ses yoksa profil sesini, profil
  // de yoksa sabit anlatıcıyı (ElevenLabs / "Dağhan Aslanger") seç.
  const AI_DEFAULT_SERVICE = "elevenlabs";
  const AI_DEFAULT_VOICE_NAME = "Dağhan Aslanger";
  const defaultSrv = profileService || AI_DEFAULT_SERVICE;
  // Google'da dil filtresi uygulanmaz (tüm sesler), ElevenLabs her zaman multilingual.
  const defaultLang =
    defaultSrv === "elevenlabs"
      ? "multilingual"
      : defaultSrv === "google"
        ? ""
        : normalizeLanguageCode(profileLanguage);
  const defaultVoiceName = profileVoiceName || AI_DEFAULT_VOICE_NAME;

  // Voice state
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedService, setSelectedService] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [samplePlayingId, setSamplePlayingId] = useState(null);
  const sampleAudioRef = useRef(null);

  // Sahnenin gerçek anlatım ses dosyası (varsa) — "Anlatıcı Ayarları" başlığının
  // yanındaki play butonuyla dinlenir.
  const [sceneAudioUrl, setSceneAudioUrl] = useState(null);
  const [scenePlaying, setScenePlaying] = useState(false);
  const sceneAudioRef = useRef(null);

  const loadVoices = useCallback(async (service, language, gender = "", page = 1) => {
    setLoadingVoices(true);
    const serviceName = API_SERVICE_MAP[service] || service;
    try {
      const params = new URLSearchParams();
      if (serviceName) params.append("service", serviceName);
      if (language) params.append("language", language);
      if (gender) params.append("gender", gender);
      params.append("page", page.toString());
      params.append("pageSize", "12");
      const response = await fetch(
        `${process.env.MW_API_URL}api/course/voices?${params.toString()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const list = Array.isArray(data.voices) ? data.voices : [];
          setVoices(list);
          if (data.totalPages) setTotalPages(data.totalPages);
          else if (data.totalCount) setTotalPages(Math.ceil(data.totalCount / 12));
          else setTotalPages(list.length >= 12 ? page + 1 : page);
          return list;
        }
      }
    } catch {
      setVoices([]);
    } finally {
      setLoadingVoices(false);
    }
    return [];
  }, []);

  // Verilen sağlayıcı/dil ile narrator default'unu set eder ve sesi
  // api/course/voices listesinde voiceId (guid) ya da isimle eşleştirip
  // gerçek objeyi seçer (AIVoiceTab loadSavedSound ile aynı kaynak/eşleştirme).
  const applyNarratorDefault = useCallback(
    (service, language, voiceId, voiceName, gender = "") => {
      if (!service) return;
      const lang = service === "elevenlabs" ? "multilingual" : language;
      const gnd = normalizeGender(gender);
      setSelectedService(service);
      setSelectedLanguage(service === "elevenlabs" ? "multilingual" : language || "");
      if (gnd) setSelectedGender(gnd);
      // Eşleşme dönene kadar kartı kayıtlı isim + cinsiyetle hemen göster — avatar
      // rengi (mavi/pembe) AIVoiceTab ile aynı olsun, cinsiyetsiz mor flash olmasın.
      setSelectedVoice({ id: voiceId || voiceName, name: voiceName || "", gender: gnd });
      loadVoices(service, lang, "", 1).then(async (list) => {
        const searchId = String(voiceId || "")
          .toLowerCase()
          .trim();
        const searchName = String(voiceName || "")
          .toLowerCase()
          .trim();
        if (!searchId && !searchName) return;
        const findIn = (arr) =>
          (arr || []).find((v) => {
            const vId = String(v.id || "")
              .toLowerCase()
              .trim();
            const vName = String(v.name || "")
              .toLowerCase()
              .trim();
            return (
              (searchId && (vId === searchId || vName === searchId)) ||
              (searchName && (vId === searchName || vName === searchName))
            );
          });
        let match = findIn(list);
        // Sayfalı liste yalnız ilk 12 sesi ve birebir dili getirir; Azure
        // MultilingualNeural sesleri (dil='multilingual') tr-TR filtresinde hiç
        // gelmez → ad id'ye yazılıp backend'e "Alessio" gidiyordu. Bulunamazsa
        // Azure kataloğunun tamamından gerçek id'yi çöz.
        if (!match && service === "azure") {
          try {
            const response = await fetch(
              `${process.env.MW_API_URL}api/course/available-voices?service=microsoft`,
              { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            if (response.ok) match = findIn((await response.json())?.voices);
          } catch {
            // eşleşme yok → aşağıdaki fallback
          }
        }
        // Eşleşme bulunursa gerçek guid'li obje seçilir; görünen isim kayıtlı
        // değerle sabitlenir, cinsiyet kayıtlı değere korunur (avatar rengi sabit).
        // Bulunmazsa ismi göster, ismi id alanına yazma.
        if (match)
          setSelectedVoice({
            ...match,
            name: voiceName || match.name,
            gender: gnd || match.gender,
          });
        else setSelectedVoice({ id: voiceId || voiceName, name: voiceName || "", gender: gnd });
      });
    },
    [loadVoices]
  );

  useEffect(() => {
    if (!show) return;
    setErrorBySceneId({});
    let cancelled = false;

    // Anlatıcı (narrator) sahnenin KAYDEDİLMİŞ sesinden gelir: editor state'teki
    // soundList[].voiceData — AIVoiceTab "Anlatım Sesi" ile aynı kaynak. getSceneAudios
    // API'si voice alanlarını her zaman döndürmüyordu ("ses bulunamadı"); voiceData kesin.
    // AIVoiceTab ile aynı kaynak: narration voiceData'da değilse narrationText/
    // narrationTextOriginal alanında olabilir (ör. InfographicSceneModal'dan üretilen
    // sahne narration'ı voiceData taşımaz). Sadece voiceData'ya bakmak yeni sahnede
    // "metin yok" yanlışına yol açıyordu.
    const target = Array.isArray(soundList)
      ? soundList.find((s) => s?.voiceData || s?.narrationText || s?.narrationTextOriginal)
      : null;
    // Çalınacak sahne ses dosyası: önce soundList girişinin dosyası, sonra API.
    setSceneAudioUrl(target?.file || null);
    setScenePlaying(false);
    if (target?.voiceData) {
      const vd = target.voiceData;
      const provider = normalizeProvider(vd.provider, vd.narratorMode);
      const lang = provider === "google" ? "" : normalizeLanguageCode(vd.language || "");
      const gnd = normalizeGender(vd.gender || "");
      if (provider === "private") {
        // Klon/own ses — course/voices listesinde yok, sadece göster.
        setSelectedService("");
        setSelectedLanguage("");
        if (gnd) setSelectedGender(gnd);
        setSelectedVoice({ id: vd.voiceId || vd.voiceName, name: vd.voiceName || "", gender: gnd });
      } else if (provider) {
        applyNarratorDefault(provider, lang, vd.voiceId || "", vd.voiceName || "", vd.gender || "");
      } else {
        // Kayıtlı sesin provider'ı çözülemedi → default anlatıcı (AIVoiceTab ile aynı).
        applyNarratorDefault(defaultSrv, defaultLang, "", defaultVoiceName, "");
      }
    } else {
      // Sahnede kayıtlı ses yok → profil sesi veya sabit default anlatıcıyı seç
      // (AIVoiceTab init default'u ile aynı).
      applyNarratorDefault(defaultSrv, defaultLang, "", defaultVoiceName, "");
    }

    // Narration metni + "Sahne Sesi" kutusu için sahne ses bilgisi. voiceData'lı
    // soundList girişi varsa onu da göster (API ses döndürmese bile metin gelsin).
    SceneAudioService.getSceneAudios()
      .then((data) => {
        if (cancelled) return;
        const scenes = Array.isArray(data?.scenes) ? data.scenes : [];
        const scene = scenes.find((s) => s.navId === navId);
        const firstAudio = scene?.audios?.find((a) => a.filePath);
        if (firstAudio) {
          setSceneAudio(firstAudio);
          if (firstAudio.filePath) setSceneAudioUrl(firstAudio.filePath);
        } else if (target?.voiceData || target?.narrationText || target?.narrationTextOriginal) {
          const vd = target.voiceData || {};
          setSceneAudio({
            narrationTextOriginal: target.narrationTextOriginal || "",
            narrationText: target.narrationText || target.narrationTextOriginal || "",
            voiceName: vd.voiceName || "",
            service: normalizeProvider(vd.provider, vd.narratorMode),
            language: vd.language || "",
            gender: vd.gender || "",
          });
        } else {
          setSceneAudio(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (target?.voiceData || target?.narrationText || target?.narrationTextOriginal) {
          const vd = target.voiceData || {};
          setSceneAudio({
            narrationTextOriginal: target.narrationTextOriginal || "",
            narrationText: target.narrationText || target.narrationTextOriginal || "",
            voiceName: vd.voiceName || "",
            service: normalizeProvider(vd.provider, vd.narratorMode),
            language: vd.language || "",
            gender: vd.gender || "",
          });
        } else {
          setSceneAudio(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [show, navId]);

  useEffect(() => {
    return () => {
      if (sampleAudioRef.current) {
        sampleAudioRef.current.pause();
        sampleAudioRef.current = null;
      }
      if (sceneAudioRef.current) {
        sceneAudioRef.current.pause();
        sceneAudioRef.current = null;
      }
    };
  }, []);

  // Modal kapanınca sahne sesini durdur.
  useEffect(() => {
    if (!show && sceneAudioRef.current) {
      sceneAudioRef.current.pause();
      sceneAudioRef.current = null;
      setScenePlaying(false);
    }
  }, [show]);

  // Modal kapatılınca DURUM KORUNUR: üretim arka planda sürer, biten sorular
  // pending'e düşer, onay bekleyenler durur. Kullanıcı Onayla/Vazgeç edene kadar
  // hiçbir şey sıfırlanmaz → tekrar açınca kaldığı yerden devam eder.

  // Başka sahneye geçince çalan sesi durdur (yeni sahnede yanlış ses çalmasın).
  useEffect(() => {
    if (sceneAudioRef.current) {
      sceneAudioRef.current.pause();
      sceneAudioRef.current = null;
      setScenePlaying(false);
    }
  }, [viewIndex]);

  const handlePlaySceneAudio = useCallback(
    (url) => {
      if (!url) return;
      if (scenePlaying && sceneAudioRef.current) {
        sceneAudioRef.current.pause();
        sceneAudioRef.current = null;
        setScenePlaying(false);
        return;
      }
      if (sampleAudioRef.current) {
        sampleAudioRef.current.pause();
        sampleAudioRef.current = null;
        setSamplePlayingId(null);
      }
      const audio = new Audio(url);
      sceneAudioRef.current = audio;
      setScenePlaying(true);
      audio.onended = () => {
        setScenePlaying(false);
        sceneAudioRef.current = null;
      };
      audio.play().catch(() => {
        setScenePlaying(false);
        sceneAudioRef.current = null;
      });
    },
    [scenePlaying]
  );

  const handleServiceChange = (service) => {
    setSelectedService(service);
    setSelectedVoice(null);
    setVoices([]);
    setCurrentPage(1);
    setTotalPages(1);
    if (service === "elevenlabs") {
      setSelectedLanguage("multilingual");
      loadVoices(service, "multilingual", "", 1);
    } else if (service) {
      // Google takes no language filter.
      const lang =
        service === "google"
          ? ""
          : pickContentLocale(
              (LANGUAGE_OPTIONS[service] || []).map((l) => l.value),
              ""
            );
      setSelectedLanguage(lang);
      setSelectedGender("");
      loadVoices(service, lang, "", 1);
    } else {
      // "Tümü" — tüm sağlayıcıların seslerini yükle (service paramı yok).
      setSelectedLanguage("");
      setSelectedGender("");
      loadVoices("", "", "", 1);
    }
  };

  const handleSamplePlay = useCallback(
    (voice) => {
      let sampleUrl = null;
      if (voice.sampleUrlsByLanguage) {
        const langs = Object.keys(voice.sampleUrlsByLanguage);
        // Örnek dil önceliği AIVoiceTab ile aynı: açık olan sayfa/editör dili >
        // TR > EN > seçili üretim dili > ilk mevcut dil.
        const lang = pickSampleLanguage(langs, { selectedLanguage });
        sampleUrl = lang && voice.sampleUrlsByLanguage[lang]?.v1;
      }
      if (!sampleUrl) sampleUrl = voice.sampleUrl || voice.audioSampleUrl || voice.previewUrl;
      if (!sampleUrl) return;

      if (samplePlayingId === voice.id && sampleAudioRef.current) {
        sampleAudioRef.current.pause();
        sampleAudioRef.current = null;
        setSamplePlayingId(null);
        return;
      }
      if (sampleAudioRef.current) sampleAudioRef.current.pause();
      const audio = new Audio(sampleUrl);
      sampleAudioRef.current = audio;
      setSamplePlayingId(voice.id);
      audio.onended = () => {
        setSamplePlayingId(null);
        sampleAudioRef.current = null;
      };
      audio.play().catch(() => {
        setSamplePlayingId(null);
        sampleAudioRef.current = null;
      });
    },
    [samplePlayingId, selectedLanguage]
  );

  // AIVoiceTab ile aynı ses alanı çıkarımı (tek/batch her iki yolda kullanılır).
  const resolveVoiceFields = () => {
    const baseService = selectedService || profileService || null;
    const providerMap = { elevenlabs: "elevenlabs", azure: "azure", google: "gemini" };
    return {
      voiceId: selectedVoice?.id || profileVoiceName || null,
      voiceName: selectedVoice?.name || profileVoiceName || null,
      gender: selectedVoice?.gender || null,
      provider: baseService ? providerMap[baseService] || baseService : null,
      language:
        baseService === "elevenlabs" ? "multilingual" : selectedLanguage || profileLanguage || null,
    };
  };

  // Tek sahnenin tip→adet kompozisyonunu item[]'e açar (ör. 2 MC + 1 DragDrop →
  // 3 item), o sahnenin narration taslağıyla. Backend items'i sırayla işler.
  const buildItemsForScene = (id) => {
    const s = scenesList.find((x) => x.id === id);
    if (!s || !s.hasNarration) return [];
    const edited = draftsById[id];
    const narration = (edited !== undefined ? edited.trim() : "") || sceneOriginalText(id);
    const items = [];
    Object.entries(sceneCounts(id)).forEach(([questionType, n]) => {
      for (let k = 0; k < (Number(n) || 0); k++)
        items.push({ navId: id, questionType, narrationText: narration });
    });
    return items;
  };

  // "combined" tab: DAHİL edilen sahnelerin (includedIds) birleşik-taslak
  // narration'ını tek metne birleştirir, COMBINED_KEY'in tip→adet kompozisyonunu
  // item[]'e açar. Anchor = seçili sahnelerin SONUNCUSU (scenesList sırasında son
  // dahil edilen) — üretilen yeni sorular o sahnenin arkasına eklenir
  // (applyGeneratedQuestion anchor mantığı). Dahil sahne yoksa navId prop'una düşer.
  const buildCombinedItems = () => {
    const included = includedIds.filter((id) =>
      scenesList.some((s) => s.id === id && s.hasNarration)
    );
    const narration = included
      .map((id) => (combinedText(id) || "").trim())
      .filter(Boolean)
      .join("\n\n");
    if (!narration.trim()) return [];
    const anchorNavId = included[included.length - 1] || navId;
    const items = [];
    Object.entries(sceneCounts(COMBINED_KEY)).forEach(([questionType, n]) => {
      for (let k = 0; k < (Number(n) || 0); k++)
        items.push({ navId: anchorNavId, questionType, narrationText: narration });
    });
    return items;
  };
  // Çoklu üretime dahil olan (narration'lı) sahnelerin id'leri — sahne sırasında.
  // Batch seviyesinde gönderilir; backend metadata.sourceNavIds'e yazar, geçmiş
  // "hangi sahnelerden" bunu okur (soru yine son sahnenin arkasına eklenir).
  const combinedSourceIds = () =>
    includedIds.filter((id) => scenesList.some((s) => s.id === id && s.hasNarration));
  const handleGenerateCombined = () =>
    handleGenerateScene(COMBINED_KEY, buildCombinedItems(), true, combinedSourceIds());

  // Tek sahne için üretim — arka planda (await sürerken kullanıcı başka sahneye
  // geçip orada da üretim başlatabilir). Sonuç sahneye YAZILMAZ; resultsBySceneId'ye
  // append edilir, sonda "Onayla" ile eklenir.
  const handleGenerateScene = async (id, itemsArg, integrative = false, sourceNavIds = null) => {
    const items = itemsArg || buildItemsForScene(id);
    if (items.length === 0) return;
    const voice = resolveVoiceFields();
    setGeneratingScenes((prev) => ({ ...prev, [id]: items.length }));
    setErrorBySceneId((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    // Kurs bağlamı: eğitimin TÜM sahnelerinin narration'ı. Backend soruyu yine o
    // sahnenin narration'ından üretir; bu sadece bağlam olarak beslenir ki soru ana
    // içerikten kopmasın/çelişmesin (yalnız bu sahneyi göndermek context kopması yaratıyordu).
    const courseContext = scenesList
      .filter((s) => s.hasNarration)
      .map((s) => s.narration)
      .join("\n\n");
    try {
      const body = {
        trainingId,
        items,
        ...(voice.voiceId && { voiceId: voice.voiceId }),
        ...(voice.provider && { provider: voice.provider }),
        ...(voice.language && { language: voice.language }),
        ...(courseContext.trim() && { courseContext }),
        ...(integrative && { integrative: true }),
        ...(sourceNavIds?.length && { sourceNavIds }),
      };
      const response = await fetch(
        `${process.env.MW_API_URL}api/question-generator/generate-batch`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(body),
        }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const { jobId } = await response.json();
      const result = await waitForJob(jobId);
      const questions = Array.isArray(result?.questions) ? result.questions : [];
      const good = questions.filter((q) => q && !q.error && q.navItem);
      if (good.length === 0) {
        setErrorBySceneId((prev) => ({
          ...prev,
          [id]: i18n.t("QuestionGenerator.NoQuestionsGenerated") || "Soru üretilemedi.",
        }));
        return;
      }
      // Append — öncekiler durur, üstüne eklenir.
      setResultsBySceneId((prev) => ({
        ...prev,
        [id]: [...(prev[id] || []), ...good.map((q) => ({ q, voice, jobId }))],
      }));
    } catch (err) {
      setErrorBySceneId((prev) => ({
        ...prev,
        [id]: err.message || i18n.t("QuestionGenerator.ErrorOccurred") || "Bir hata oluştu",
      }));
    } finally {
      setGeneratingScenes((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      // Seçim üretim boyunca kartlarda görünür kaldı; bitince temizle (append için sıfırdan).
      setCountsBySceneId((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  // Bir review sorusunun seçili (tik'li) olup olmadığı. Varsayılan seçili.
  const isResultSelected = (id, index) => !(deselectedBySceneId[id] || []).includes(index);
  // Tik aç/kapa (soru bazlı seçim). Kart görünürde kalır; sadece seçim değişir.
  const toggleResultSelected = (id, index) =>
    setDeselectedBySceneId((prev) => {
      const arr = prev[id] || [];
      const nextArr = arr.includes(index) ? arr.filter((x) => x !== index) : [...arr, index];
      return { ...prev, [id]: nextArr };
    });
  // Sahnede seçili (eklenecek) soru sayısı.
  const selectedCount = (id) =>
    (resultsBySceneId[id] || []).reduce((n, _e, i) => n + (isResultSelected(id, i) ? 1 : 0), 0);

  // Onayla (per-sahne) → o sahnenin SEÇİLİ (tik'li) sorularını sahneye ekle (handler
  // navItemSelected+1 kullandığı için reverse ile orijinal sıra korunur), pending'i
  // temizle, sonra MODALI KAPAT → user eklenen soruları editörde görsün.
  const handleApproveScene = (id) => {
    const entries = resultsBySceneId[id] || [];
    const selected = entries.filter((_e, i) => isResultSelected(id, i));
    if (selected.length === 0) return;
    // Bu sahneyi besleyen tüm job'ları onayla (seçilmeyen sorular dahil —
    // kullanıcı sonuca karar verdi); widget'taki "Onay Bekliyor" kartları kapanır
    // ve widget'tan ikinci kez eklenmez.
    [...new Set(entries.map((e) => e.jobId).filter(Boolean))].forEach(approveJob);
    selected
      .slice()
      .reverse()
      .forEach(({ q, voice }) =>
        onQuestionGenerated?.({
          ...q,
          _voiceId: voice.voiceId,
          _voiceName: voice.voiceName,
          _provider: voice.provider,
          _language: voice.language,
          _gender: voice.gender,
          _narrationText: q?.questionText ?? "",
        })
      );
    setResultsBySceneId((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setDeselectedBySceneId((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    // Eklendi → modalı kapat ki kullanıcı sahnelere eklenen soruları editörde görsün.
    onClose?.();
  };

  // Vazgeç (per-sahne) → onay bekleyen soruları at, oluşturma ekranına dön (eklenmez).
  const handleDiscardScene = (id) => {
    setResultsBySceneId((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setDeselectedBySceneId((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // Geçmişi backend'den çeker (GET /history). Hem modal açılışında (kalıcı
  // "eklenen soru" sayısı + tik için) hem de panel her açılışında çağrılır.
  const loadHistory = useCallback(() => {
    if (!trainingId) return;
    setHistoryLoading(true);
    fetch(
      `${process.env.MW_API_URL}api/question-generator/history?trainingId=${encodeURIComponent(
        trainingId
      )}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    )
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data) => {
        setHistoryItems(Array.isArray(data?.items) ? data.items : []);
        setHistoryLoadedFor(trainingId);
      })
      .catch(() => setHistoryItems([]))
      .finally(() => setHistoryLoading(false));
  }, [trainingId]);

  // Modal açılınca geçmişi bir kez çek — böylece "bu sahneye eklenen soru: N" notu
  // ve yeşil tik, panel açılmasa da (ve refresh sonrası da) doğru gösterilir.
  useEffect(() => {
    if (show) {
      loadHistory();
      // Oturum-içi çift-ekleme kilidini sıfırla → navItemsIds gerçeğiyle re-sync
      // (silinip yeniden eklenmek istenen soru engellenmesin).
      setAddedHistoryIds([]);
      // Modal her açılışta NORMAL question ekranıyla gelsin — geçmiş kapalı
      // (kullanıcı önceki oturumda geçmişi açık bırakıp kapattıysa açık gelmesin).
      setHistoryOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, trainingId]);

  // Geçmiş panelini aç/kapat. Her AÇILIŞTA yeniden fetch (cache YOK) — panele her
  // bastığında güncel liste gelsin (yeni üretilen/eklenen sorular görünsün).
  const toggleHistory = () => {
    const willOpen = !historyOpen;
    setHistoryOpen(willOpen);
    if (!willOpen || historyLoading) return;
    loadHistory();
  };

  // Bir geçmiş sorusu eğitimde/eklenmiş mi? navItemsIds (kurstaki sahneler) VEYA bu
  // oturumda eklenenler (addedHistoryIds — navItemsIds prop'u tazelenene kadarki
  // pencereyi kapatır). İkisi de true → tekrar EKLENMEZ.
  const isHistoryAdded = (item) =>
    (!!item?.payload?.navItem?.id && (navItemsIds || []).includes(item.payload.navItem.id)) ||
    addedHistoryIds.includes(item?.id);

  // Geçmişteki soruyu o an SEÇİLİ sahneye ekle (onQuestionGenerated navItemSelected+1
  // kullanır — per-sahne Onayla ile aynı akış). Saklanan payload birebir üretim
  // yanıtı; voice payload'da (soundList) zaten var. Modal AÇIK kalır (WIP tutarlı).
  // ÇİFT EKLEME ENGELİ: zaten eklenmişse (isHistoryAdded) tekrar tıklama yok sayılır.
  const addHistoryToScene = (item) => {
    if (!item?.payload?.navItem?.id) return;
    if (isHistoryAdded(item)) return;
    onQuestionGenerated?.({
      ...item.payload,
      _narrationText: item.payload?.questionText ?? "",
    });
    setAddedHistoryIds((prev) => [...prev, item.id]);
    // item.id = "<jobId>:<i>" — kart hâlâ bekliyorsa widget'ta kapansın (idempotent).
    approveJob(String(item.id || "").split(":")[0]);
    // Eklendi → modalı kapat ki kullanıcı eklenen soruyu editörde görsün.
    onClose?.();
  };

  // Bir sahnenin tip adedini değiştir (0 alt sınır). 0 olan tip ve boş kalan
  // sahne haritadan silinir.
  const setTypeCount = (sceneId, type, n) =>
    setCountsBySceneId((prev) => {
      const cur = { ...(prev[sceneId] || {}) };
      const v = Math.max(0, n);
      if (v === 0) delete cur[type];
      else cur[type] = v;
      const next = { ...prev };
      if (Object.keys(cur).length === 0) delete next[sceneId];
      else next[sceneId] = cur;
      return next;
    });

  // Seçili ses: kayıtlı sahne sesi, profil sesi veya sabit default anlatıcı
  // (AIVoiceTab ile aynı öncelik). applyNarratorDefault selectedVoice'u set eder.
  const effectiveVoiceName = selectedVoice?.name || "";
  const effectiveService = selectedService;
  const effectiveLanguage = selectedLanguage;

  // Gösterilen sahne arka planda üretiliyor mu? (main yerine spinner gösterilir.)
  const viewGenerating = !!(viewedId && generatingScenes[viewedId]);
  // Gösterilen sahnede onay bekleyen sorular var mı? (main yerine review gösterilir.)
  const viewReview = !viewGenerating && generatedCount(viewedId) > 0;
  // Soru tipi gridi: narration'ı olan ve o an üretilmeyen sahnede düzenlenebilir.
  const qtypeDisabled = !viewedId || !viewedScene?.hasNarration || viewGenerating;

  // Combined tab durumu (COMBINED_KEY üzerinden — per-sahne state reuse).
  const isCombined = activeTab === "combined";
  // İnteraktif sekmesi: soru ÜRETMEZ, aktivite üretir. Bu modalın sayaç/üretim
  // makinesinin hiçbirini kullanmaz — sol sahne listesini ve seçilen sahnenin
  // anlatım taslağını kullanır, gerisini kendi paneli yürütür. Bu yüzden
  // aşağıdaki footer dallarının hepsinde ayrıca dışarıda tutulur: "Oluştur"
  // düğmesi bu sekmede soru üretmeye kalkışırsa, kullanıcının hiç istemediği
  // bir üretimi ücretlendirir.
  const isInteractive = activeTab === "interactive";
  const combinedGenerating = !!generatingScenes[COMBINED_KEY];
  const combinedReview = !combinedGenerating && generatedCount(COMBINED_KEY) > 0;
  // Footer'ın dallandığı aktif anahtar/durumlar (tab'a göre).
  const activeKey = isCombined ? COMBINED_KEY : viewedId;
  const activeGenerating = isCombined ? combinedGenerating : viewGenerating;
  const activeReview = isCombined ? combinedReview : viewReview;
  // Combined tab'da en az bir narration'lı sahne dahil edilmiş mi?
  const combinedHasSource = includedIds.some((id) =>
    scenesList.some((s) => s.id === id && s.hasNarration)
  );
  // Soru tipi gridi düzenlenebilir mi? (aktif tab'a göre.)
  const mainQtypeDisabled = isCombined ? combinedGenerating || !combinedHasSource : qtypeDisabled;

  // ── SOL LİSTE: GEZİNME Mİ, SEÇİM Mİ ──────────────────────────────────────
  //
  // "Tek Sahneden"de sol liste bir GEZİNME aracı: tıklarsın, o sahnenin
  // anlatımı sağda açılır. "Çoklu Anlatımdan" ve "İnteraktif"te ise bir SEÇİM
  // aracı: tıkladığın sahne kaynağa katılır, anahtarı açık kalır.
  //
  // İkisi tek bayrakla ifade ediliyor çünkü listenin üç ayrı yerinde
  // (vurgulama, tıklama, sağdaki anahtar) aynı soruya cevap veriliyor; üçünü
  // ayrı ayrı `isCombined || isInteractive` diye yazmak, birini eklemeyi
  // unutmanın en kolay yolu olurdu.
  const picksScenes = isCombined || isInteractive;

  // İnteraktif sekmesinde kaynağa katılan (anlatımı olan) sahneler.
  const interactiveIncluded = includedIds.filter((id) =>
    scenesList.some((s) => s.id === id && s.hasNarration)
  );
  // Aktivitenin DÜŞECEĞİ sahne: seçilenlerin SONUNCUSU — "Çoklu Anlatımdan"ın
  // üretilen soruları son sahnenin arkasına koyan kuralıyla aynı
  // (buildCombinedItems/anchorNavId). Hiç seçim yoksa görüntülenen sahne.
  const interactiveAnchorId = interactiveIncluded[interactiveIncluded.length - 1] || viewedId;
  const interactiveAnchor = scenesList.find((s) => s.id === interactiveAnchorId) || viewedScene;

  // Kaynak sahne adı çözümü (review kartları için).
  const sceneNameById = scenesList.reduce((acc, s) => {
    acc[s.id] = s.name;
    return acc;
  }, {});
  // Sahne id → gerçek pozisyon numarası (soru sahneleri atlanmış; scenesList.index).
  // Çoklu kaynak rozetinde kompakt "Sahne 1,2,5" için isim yerine numara kullanılır.
  const sceneIndexById = scenesList.reduce((acc, s) => {
    acc[s.id] = s.index;
    return acc;
  }, {});

  return (
    <AppModal
      show={show}
      onHide={onClose}
      onClose={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <span>{i18n.t("QuestionGenerator.Title") || "Soru Üretici"}</span>
          <div className="infographic-modal__tabs infographic-modal__tabs--inline">
            {[
              { id: "single", label: i18n.t("QuestionGenerator.TabSingle") || "Tek Sahneden" },
              {
                id: "combined",
                label: i18n.t("QuestionGenerator.TabCombined") || "Çoklu Anlatımdan",
              },
              {
                id: "interactive",
                label: i18n.t("QuestionGenerator.TabInteractive") || "İnteraktif",
              },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`infographic-modal__tab${
                  activeTab === tab.id ? " infographic-modal__tab--active" : ""
                }`}
              >
                <span className="infographic-modal__tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      }
      // Footer moda göre: form → Oluştur; üretim → "Oluşturuluyor..." (pasif,
      // kaybolmaz); review → Reddet + Onayla. Aktif tab'a göre anahtar (activeKey).
      cancelLabel={
        !historyOpen && !isInteractive && activeReview ? i18n.t("Reject") || "Reddet" : undefined
      }
      onCancel={
        !historyOpen && activeReview
          ? () => activeKey && handleDiscardScene(activeKey)
          : onCancel || onClose
      }
      cancelDisabled={false}
      confirmLabel={
        // İnteraktif sekmesinin kendi "Sahneye ekle" düğmesi var; modalın
        // "Oluştur"u burada soru üretmeye kalkışırdı.
        historyOpen || isInteractive
          ? undefined
          : activeReview
            ? i18n.t("Approve") || "Onayla"
            : activeGenerating
              ? i18n.t("Generating") || "Oluşturuluyor..."
              : i18n.t("Generate") || "Oluştur"
      }
      onConfirm={
        activeReview
          ? () => activeKey && handleApproveScene(activeKey)
          : isCombined
            ? handleGenerateCombined
            : () => viewedId && handleGenerateScene(viewedId)
      }
      confirmDisabled={
        activeReview
          ? selectedCount(activeKey) === 0
          : isCombined
            ? combinedGenerating || buildCombinedItems().length === 0
            : viewGenerating || qtypeDisabled || sceneTotal(viewedId) === 0
      }
      size="xl"
      className="questionGeneratorModal"
      bodyProps={{ className: "qgen-modal__body" }}
    >
      {
        <div className="qgen-modal">
          {/* Soru önizleme overlay'i — review kartındaki göz ikonuyla açılır; soruyu
          eklemeden önce tam gör (tip + metin + cevaplar, doğru vurgulu). */}
          {previewQuestion &&
            (() => {
              const sm = summarizeQuestion(previewQuestion, sceneNameById);
              const tl = sm.type ? i18n.t(`${sm.type}.PluginName`) || sm.type : "";
              return (
                <div
                  onClick={() => setPreviewQuestion(null)}
                  style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(15,23,42,0.55)",
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 20,
                  }}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      maxWidth: 560,
                      width: "100%",
                      maxHeight: "80vh",
                      overflowY: "auto",
                      padding: 20,
                      position: "relative",
                      boxShadow: "0 24px 60px rgba(15,23,42,0.3)",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setPreviewQuestion(null)}
                      title={i18n.t("Close") || "Kapat"}
                      style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        border: "none",
                        background: "#f1f5f9",
                        color: "#334155",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <LuX size={16} />
                    </button>
                    {tl && (
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#7c3aed",
                          marginBottom: 8,
                        }}
                      >
                        {tl}
                      </div>
                    )}
                    <p
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: "#0f172a",
                        marginBottom: 12,
                        paddingRight: 28,
                      }}
                    >
                      {sm.text}
                    </p>
                    {sm.answers.length > 0 && (
                      <ul
                        style={{
                          listStyle: "none",
                          padding: 0,
                          margin: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        {sm.answers.map((a, j) => (
                          <li
                            key={j}
                            style={{
                              padding: "8px 12px",
                              borderRadius: 8,
                              fontSize: 14,
                              background: a.correct ? "#dcfce7" : "#f8fafc",
                              border: a.correct ? "1px solid #86efac" : "1px solid #e2e8f0",
                              color: "#0f172a",
                            }}
                          >
                            {a.text}
                            {a.correct && (
                              <span style={{ marginLeft: 6, color: "#16a34a", fontWeight: 700 }}>
                                ✓ {i18n.t("QuestionGenerator.CorrectMark") || "Doğru"}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })()}
          {/* Soru geçmişi — Görsel Animatör "Geçmiş" tasarımı: üstte açılır şerit +
          kare kart grid'i (her kart bir soru tipi preview'i). Karta tıkla = o soruyu
          seçili sahneye ekle. LAZY: ilk açılışta bir kez fetch. qgen-modal__body
          padding'i 0 (layout edge-to-edge) → şeridi animatör gibi inset yapmak için
          buraya padding veriyoruz (kenara/tepeye yapışmasın). */}
          <div style={{ padding: "12px 16px 4px" }}>
            <button
              type="button"
              onClick={toggleHistory}
              disabled={!trainingId}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                background: "#f8fafc",
                color: "#475569",
                fontSize: 12,
                fontWeight: 600,
                cursor: trainingId ? "pointer" : "not-allowed",
              }}
            >
              <LuHistory size={14} />
              <span style={{ flex: 1, textAlign: "left" }}>
                {i18n.t("QuestionGenerator.History") || "Geçmiş"}
              </span>
              <span style={{ fontSize: 10 }}>{historyOpen ? "▾" : "▸"}</span>
            </button>

            {historyOpen && historyLoading && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  padding: 22,
                  border: "1px solid #f1f5f9",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  color: "#64748b",
                  fontSize: 12,
                }}
              >
                {i18n.t("Loading") || "Yükleniyor"}...
              </div>
            )}

            {historyOpen && !historyLoading && historyLoadedFor && historyItems.length === 0 && (
              <div
                style={{
                  padding: 18,
                  textAlign: "center",
                  border: "1px solid #f1f5f9",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  color: "#94a3b8",
                  fontSize: 12,
                }}
              >
                {i18n.t("QuestionGenerator.NoHistory") || "Geçmiş soru yok."}
              </div>
            )}

            {historyOpen && !historyLoading && historyItems.length > 0 && (
              <div
                style={{
                  border: "1px solid #f1f5f9",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 14,
                    padding: "10px 12px 12px",
                    maxHeight: 440,
                    overflowY: "auto",
                  }}
                >
                  {historyItems.map((item) => {
                    const def = QUESTION_TYPE_DEFS.find((d) => d.value === item.questionType);
                    const label = def
                      ? i18n.t(def.nameKey) || def.fallback
                      : item.questionType || "";
                    const qtext =
                      item.questionText ||
                      summarizeQuestion(item.payload, sceneNameById).text ||
                      "";
                    // Çoklu anlatımdan üretilmişse tüm kaynak sahneleri KOMPAKT göster:
                    // numaraları birleştir → "Sahne 1,2,5" (isim tek tek sığmıyor).
                    // Numarası çözülemeyen id atlanır; hiçbiri yoksa tek kaynak adına düşer.
                    const sourceNums = Array.isArray(item.sourceNavIds)
                      ? item.sourceNavIds.map((id) => sceneIndexById[id]).filter(Boolean)
                      : [];
                    const sceneName =
                      sourceNums.length > 0
                        ? `${i18n.t("Scene") || "Sahne"} ${sourceNums.join(",")}`
                        : sceneNameById[item.navId] || "";
                    // Zaten eklenmiş mi? Eğitimde var (navItemsIds) VEYA bu oturumda
                    // eklendi (addedHistoryIds). Eklenmişse tik gösterilir, "+" gizlenir,
                    // tekrar tıklama eklemez (addHistoryToScene guard'lı).
                    const added = isHistoryAdded(item);
                    return (
                      <div
                        key={item.id}
                        style={{
                          flex: "0 0 auto",
                          width: 138,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <button
                          type="button"
                          title={
                            added
                              ? i18n.t("QuestionGenerator.AlreadyInTraining") || "Eğitimde mevcut"
                              : qtext || label
                          }
                          onClick={() => !added && addHistoryToScene(item)}
                          style={{
                            width: 132,
                            height: 132,
                            padding: 10,
                            borderRadius: 10,
                            overflow: "hidden",
                            cursor: added ? "default" : "pointer",
                            position: "relative",
                            background: "#f8fafc",
                            border: added ? "2px solid #86efac" : "2px solid #e2e8f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {def?.preview ? (
                            <img
                              src={def.preview}
                              alt={label}
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          ) : (
                            <span
                              className="material-icons"
                              style={{ fontSize: 60, color: "#94a3b8" }}
                            >
                              {def?.icon || "quiz"}
                            </span>
                          )}
                          {sceneName && (
                            <span
                              title={sceneName}
                              style={{
                                position: "absolute",
                                top: 3,
                                left: 3,
                                maxWidth: 74,
                                padding: "0 4px",
                                height: 16,
                                borderRadius: 4,
                                fontSize: 9,
                                fontWeight: 700,
                                color: "#fff",
                                background: "rgba(15,23,42,0.72)",
                                display: "flex",
                                alignItems: "center",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {sceneName}
                            </span>
                          )}
                          {/* Önizle — kare sol altta göz; tıklayınca soru overlay'de
                          açılır (stopPropagation → sahneye EKLEMEZ). */}
                          <span
                            role="button"
                            title={i18n.t("Preview") || "Önizle"}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewQuestion(item.payload);
                            }}
                            style={{
                              position: "absolute",
                              bottom: 3,
                              left: 3,
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              background: "rgba(15,23,42,0.72)",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              zIndex: 2,
                            }}
                          >
                            <LuEye size={11} />
                          </span>
                          {added && (
                            <span
                              style={{
                                position: "absolute",
                                top: 3,
                                right: 3,
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background: "#16a34a",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title={
                                i18n.t("QuestionGenerator.AlreadyInTraining") || "Eğitimde mevcut"
                              }
                            >
                              <LuCheck size={11} />
                            </span>
                          )}
                          {!added && (
                            <span
                              style={{
                                position: "absolute",
                                bottom: 3,
                                right: 3,
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                background: "#6366f1",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 15,
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                              title={i18n.t("AddToScene") || "Sahneye ekle"}
                            >
                              +
                            </span>
                          )}
                        </button>
                        <span
                          style={{
                            fontSize: 13,
                            lineHeight: "16px",
                            fontWeight: 600,
                            color: "#334155",
                            textAlign: "center",
                            width: "100%",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {label}
                        </span>
                        {qtext && (
                          <span
                            style={{
                              fontSize: 11,
                              lineHeight: "14px",
                              color: "#94a3b8",
                              textAlign: "center",
                              width: "100%",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {qtext}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {/* Geçmiş AÇIKKEN alttaki düzen (sahne listesi + form) gizlenir; geçmişi
          kapatınca geri gelir (Görsel Animatör davranışı). */}
          <div className="qgen-modal__layout" style={{ display: historyOpen ? "none" : "flex" }}>
            {/* Sol sahne paneli (AddSoundModal tasarımı) — sahneler arası geçiş.
            Toggle yok: her satır üretim durumunu (seçili adet / üretiliyor /
            üretildi) gösterir. */}
            {scenesList.length > 1 && (
              <div className="qgen-modal__scene-list">
                <div className="qgen-modal__scene-list-header">
                  {i18n.t("Scenes") || "Sahneler"}
                </div>
                {scenesList.map((s, i) => (
                  <div
                    key={s.id}
                    className={`qgen-modal__scene-item${
                      !s.hasNarration ? " qgen-modal__scene-item--disabled" : ""
                    }${
                      (picksScenes ? includedIds.includes(s.id) : i === viewIndex)
                        ? " qgen-modal__scene-item--active"
                        : ""
                    }`}
                    title={s.hasNarration ? s.name : i18n.t("NoSoundInScene") || "Ses yok"}
                    onClick={() => {
                      if (picksScenes) {
                        if (s.hasNarration) toggleInclude(s.id);
                      } else setViewIndex(i);
                    }}
                  >
                    <span className="qgen-modal__scene-main">
                      <span className="qgen-modal__scene-index">{s.index}</span>
                      <span className="qgen-modal__scene-name">{s.name}</span>
                      {/* Seçim sekmelerinde (Çoklu / İnteraktif) her sahne
                      yanında DAHİL ET toggle'ı (text-in-image toggle stili).
                      Tek-sahne tab'da üretim durumu. */}
                      {picksScenes ? (
                        <span
                          role="switch"
                          aria-checked={includedIds.includes(s.id)}
                          title={i18n.t("QuestionGenerator.IncludeScene") || "Soruya dahil et"}
                          style={{
                            marginLeft: "auto",
                            width: 34,
                            height: 18,
                            borderRadius: 999,
                            background: includedIds.includes(s.id) ? "#7c3aed" : "#cbd5e1",
                            padding: 2,
                            flexShrink: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            opacity: s.hasNarration ? 1 : 0.4,
                          }}
                        >
                          <span
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              background: "#fff",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                              transition: "transform 0.2s ease",
                              transform: includedIds.includes(s.id)
                                ? "translateX(16px)"
                                : "translateX(0)",
                            }}
                          />
                        </span>
                      ) : generatingScenes[s.id] ? (
                        <span
                          className="qgen-modal__scene-spinner"
                          title={i18n.t("QuestionGenerator.GeneratingQuestions") || "Üretiliyor..."}
                        />
                      ) : generatedCount(s.id) > 0 ? (
                        <span
                          className="qgen-modal__scene-pending"
                          title={i18n.t("QuestionGenerator.PendingApproval") || "Onay bekliyor"}
                        >
                          {generatedCount(s.id)}
                        </span>
                      ) : committedCount(s.id) > 0 ? (
                        <span
                          className="qgen-modal__scene-done"
                          title={i18n.t("QuestionGenerator.Added") || "Eklendi"}
                        >
                          <LuCheck size={12} />
                          {committedCount(s.id)}
                        </span>
                      ) : sceneTotal(s.id) > 0 ? (
                        <span className="qgen-modal__scene-count">{sceneTotal(s.id)}</span>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="qgen-modal__main">
              {isInteractive ? (
                // Sol sahne listesi host'un, aktivite akışı panelin.
                // Host yalnızca SEÇİMİ bildirir (hangi sahneler, hangisine
                // düşecek); metni panel kendi kutusunda kurar ve orada
                // düzenlenir — bu sekmede host'un anlatım taslakları
                // (viewedDraft / combinedDraftsById) hiç kullanılmıyor.
                <InteractivePane
                  // Aktivitenin düşeceği sahne = seçilenlerin SONUNCUSU
                  // ("Çoklu Anlatımdan"ın anchor kuralı). Pane bunu ekranda
                  // yazıyor, yazarın tahmin etmesi gerekmiyor.
                  navId={interactiveAnchorId}
                  sceneTitle={interactiveAnchor?.name}
                  sceneIndex={interactiveAnchor?.index}
                  trainingId={trainingId}
                  // Kaynak metin sol listeden seçilen sahnelerden kuruluyor.
                  scenes={scenesList}
                  includedIds={interactiveIncluded}
                  onInserted={onClose}
                />
              ) : activeReview ? (
                // Üretim bitince: o sahnenin üretilen soruları burada listelenir.
                // Onayla → sahneye ekler + temizler → oluşturma ekranına döner.
                <div className="qgen-modal__scene-review">
                  <div className="qgen-modal__section-header">
                    <p className="qgen-modal__section-label">
                      {i18n.t("QuestionGenerator.Review") || "Üretilen sorular"} (
                      {generatedCount(activeKey)})
                    </p>
                  </div>
                  <div className="qgen-modal__review-list">
                    {(resultsBySceneId[activeKey] || []).map(({ q }, i) => {
                      const sm = summarizeQuestion(q, sceneNameById);
                      const typeLabel = sm.type ? i18n.t(`${sm.type}.PluginName`) || sm.type : "";
                      const selected = activeKey ? isResultSelected(activeKey, i) : true;
                      return (
                        <div
                          key={i}
                          className="qgen-modal__review-card"
                          style={{ opacity: selected ? 1 : 0.5 }}
                        >
                          <div className="qgen-modal__review-head">
                            {typeLabel && (
                              <span className="qgen-modal__review-type">{typeLabel}</span>
                            )}
                            {/* Soru bazlı onay toggle'ı (VideoAIGenerateTab __ai-toggle
                            tasarımı) — hepsi AÇIK gelir; kapatınca o soru Onayla'da
                            EKLENMEZ (kart soluklaşır). Yanında "Soru onaylıyorum" text. */}
                            <span
                              style={{
                                marginLeft: "auto",
                                fontSize: 12,
                                fontWeight: 600,
                                color: selected ? "#7c3aed" : "#94a3b8",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {i18n.t("QuestionGenerator.ApproveThisQuestion") ||
                                "Soruyu onaylıyorum"}
                            </span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={selected}
                              onClick={() => activeKey && toggleResultSelected(activeKey, i)}
                              title={
                                i18n.t("QuestionGenerator.ApproveThisQuestion") ||
                                "Soruyu onaylıyorum"
                              }
                              style={{
                                marginLeft: 8,
                                width: 36,
                                height: 20,
                                borderRadius: 999,
                                background: selected ? "#7c3aed" : "#cbd5e1",
                                border: "none",
                                padding: 2,
                                cursor: "pointer",
                                position: "relative",
                                flexShrink: 0,
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: "50%",
                                  background: "#ffffff",
                                  display: "block",
                                  boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                                  transition: "transform 0.2s ease",
                                  transform: selected ? "translateX(16px)" : "translateX(0)",
                                }}
                              />
                            </button>
                          </div>
                          <p className="qgen-modal__review-q">{sm.text}</p>
                          {sm.answers.length > 0 && (
                            <ul className="qgen-modal__review-answers">
                              {sm.answers.map((a, j) => (
                                <li
                                  key={j}
                                  className={`qgen-modal__review-answer${
                                    a.correct ? " qgen-modal__review-answer--correct" : ""
                                  }`}
                                >
                                  {a.text}
                                  {a.correct && (
                                    <span className="qgen-modal__review-correct">
                                      {" "}
                                      ✓ {i18n.t("QuestionGenerator.CorrectMark") || "Doğru"}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <>
                  {isCombined ? (
                    // Combined tab: DAHİL edilen her sahnenin narration'ı AYRI
                    // düzenlenebilir textbox olarak (orijinal sahne değişmez).
                    <>
                      {!combinedHasSource ? (
                        <p className="qgen-modal__empty">
                          {i18n.t("QuestionGenerator.SelectScenesHint") ||
                            "Sol listeden en az bir sahne seçin."}
                        </p>
                      ) : (
                        includedIds
                          .filter((id) => scenesList.some((s) => s.id === id && s.hasNarration))
                          .map((id) => {
                            const meta = scenesList.find((s) => s.id === id);
                            const orig = sceneOriginalText(id);
                            const val = combinedText(id);
                            return (
                              <div key={id} style={{ marginBottom: 10 }}>
                                <div className="qgen-modal__section-header">
                                  <p className="qgen-modal__section-label" style={{ fontSize: 12 }}>
                                    {meta ? meta.name : id}
                                  </p>
                                  <button
                                    type="button"
                                    className="qgen-modal__restore-btn"
                                    onClick={() => setCombinedDraft(id, orig)}
                                    disabled={combinedGenerating}
                                    style={{ visibility: val !== orig ? "visible" : "hidden" }}
                                  >
                                    {i18n.t("RestoreNarrationText") || "Anlatım metnini getir"}
                                  </button>
                                </div>
                                <textarea
                                  className="qgen-modal__textarea"
                                  value={val}
                                  onChange={(e) => setCombinedDraft(id, e.target.value)}
                                  disabled={combinedGenerating}
                                  rows={3}
                                />
                              </div>
                            );
                          })
                      )}
                    </>
                  ) : (
                    <>
                      {/* Narration — sol panelden seçilen sahnenin düzenlenebilir taslağı */}
                      <div className="qgen-modal__section-header">
                        <p className="qgen-modal__section-label">
                          {i18n.t("NarrationText") || "Narration Text"}
                        </p>
                        <button
                          type="button"
                          className="qgen-modal__restore-btn"
                          onClick={() => viewedId && setDraft(viewedId, viewedOriginal)}
                          disabled={viewGenerating}
                          style={{
                            visibility: viewedDraft !== viewedOriginal ? "visible" : "hidden",
                          }}
                        >
                          {i18n.t("RestoreNarrationText") || "Anlatım metnini getir"}
                        </button>
                      </div>

                      {!viewedDraft && !viewedOriginal ? (
                        <p className="qgen-modal__empty">
                          {i18n.t("NoSoundInScene") || "Bu sahnede henüz ses bulunmuyor."}
                        </p>
                      ) : (
                        <textarea
                          className="qgen-modal__textarea"
                          value={viewedDraft}
                          onChange={(e) => viewedId && setDraft(viewedId, e.target.value)}
                          disabled={viewGenerating || !viewedScene?.hasNarration}
                          rows={3}
                        />
                      )}
                    </>
                  )}

                  {/* Voice selector */}
                  <p className="qgen-modal__section-label">
                    {i18n.t("NarratorSettings") || "Anlatıcı Ayarları"}
                  </p>
                  <div className="qgen-modal__voice-profile">
                    <div className="qgen-modal__voice-row">
                      <div
                        className="qgen-modal__voice-avatar"
                        style={genderAvatarStyle(selectedVoice?.gender)}
                      >
                        {(effectiveVoiceName || "?")[0].toUpperCase()}
                      </div>
                      <div className="qgen-modal__voice-details">
                        <div className="qgen-modal__voice-title-row">
                          <span className="qgen-modal__voice-name">
                            {effectiveVoiceName || "-"}
                          </span>
                          {effectiveService && (
                            <span className="qgen-modal__voice-pill">
                              {SERVICE_LABELS[effectiveService] || effectiveService}
                            </span>
                          )}
                          {selectedVoice?.gender && (
                            <span className="qgen-modal__voice-pill">
                              {normalizeGender(selectedVoice.gender) === "male"
                                ? i18n.t("Male") || "Erkek"
                                : i18n.t("Female") || "Kadın"}
                            </span>
                          )}
                          {effectiveLanguage && (
                            <span className="qgen-modal__voice-pill">{effectiveLanguage}</span>
                          )}
                        </div>
                      </div>
                      {viewedAudioUrl && (
                        <button
                          type="button"
                          className="qgen-modal__scene-play"
                          onClick={() => handlePlaySceneAudio(viewedAudioUrl)}
                          title={i18n.t("Play") || "Dinle"}
                          aria-label={i18n.t("Play") || "Dinle"}
                        >
                          {scenePlaying ? <LuPause size={14} /> : <LuPlay size={14} />}
                        </button>
                      )}
                      <button
                        type="button"
                        className="qgen-modal__voice-action"
                        onClick={() => setProfileOpen((prev) => !prev)}
                      >
                        {i18n.t("ChangeNarrator") || "Anlatıcıyı değiştir"}
                        <span className="qgen-modal__voice-action-icon">
                          {profileOpen ? "▲" : "▼"}
                        </span>
                      </button>
                    </div>

                    {profileOpen && (
                      <div className="qgen-modal__voice-panel">
                        <div className="qgen-modal__voice-filters">
                          {/* Sağlayıcı — chip (AIVoiceTab tasarımı) */}
                          <div className="qgen-modal__voice-filter">
                            <div className="qgen-modal__voice-filter-label">
                              {i18n.t("Provider") || "Sağlayıcı"}
                            </div>
                            <div className="qgen-modal__voice-filter-chips">
                              {[
                                { value: "", label: i18n.t("AllProviders") || "Tümü" },
                                { value: "elevenlabs", label: "ElevenLabs" },
                                { value: "google", label: "Google" },
                                { value: "azure", label: "Azure" },
                                { value: "sestek", label: "Sestek" },
                              ]
                                // "Sestek" sağlayıcı çipi gizlendi — silinmedi, render'da filtrelendi.
                                .filter((p) => p.value !== "sestek")
                                .map((p) => (
                                  <button
                                    key={p.value || "all"}
                                    type="button"
                                    className={`qgen-modal__voice-filter-chip qgen-modal__voice-filter-chip--provider${
                                      selectedService === p.value
                                        ? " qgen-modal__voice-filter-chip--on"
                                        : ""
                                    }`}
                                    onClick={() => handleServiceChange(p.value)}
                                    disabled={viewGenerating}
                                  >
                                    {p.label}
                                  </button>
                                ))}
                            </div>
                          </div>

                          {/* Dil dropdownu — ElevenLabs ve Google hariç (Google'da dil seçimi yok) */}
                          {selectedService &&
                            selectedService !== "elevenlabs" &&
                            selectedService !== "google" && (
                              <div className="qgen-modal__voice-filter">
                                <div className="qgen-modal__voice-filter-label">
                                  {i18n.t("language") || "Language"}
                                </div>
                                <div className="qgen-modal__voice-select-input">
                                  <select
                                    value={selectedLanguage}
                                    onChange={(e) => {
                                      setSelectedLanguage(e.target.value);
                                      setCurrentPage(1);
                                      loadVoices(
                                        selectedService,
                                        e.target.value,
                                        selectedGender,
                                        1
                                      );
                                    }}
                                    disabled={viewGenerating}
                                  >
                                    <option value="">
                                      {i18n.t("SelectLanguage") || "Bir dil seçiniz"}
                                    </option>
                                    {(LANGUAGE_OPTIONS[selectedService] || []).map((l) => (
                                      <option key={l.value} value={l.value}>
                                        {l.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            )}

                          {/* Cinsiyet — chip (AIVoiceTab tasarımı) */}
                          <div className="qgen-modal__voice-filter qgen-modal__voice-filter--gender">
                            <div className="qgen-modal__voice-filter-label">
                              {i18n.t("Gender") || "Gender"}
                            </div>
                            <div className="qgen-modal__voice-filter-chips">
                              {[
                                { value: "", label: i18n.t("AllGenders") || "Tümü" },
                                { value: "female", label: i18n.t("Female") || "Kadın" },
                                { value: "male", label: i18n.t("Male") || "Erkek" },
                              ].map((g) => (
                                <button
                                  key={g.value || "all"}
                                  type="button"
                                  className={`qgen-modal__voice-filter-chip${
                                    selectedGender === g.value
                                      ? " qgen-modal__voice-filter-chip--on"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    setSelectedGender(g.value);
                                    setCurrentPage(1);
                                    loadVoices(selectedService, selectedLanguage, g.value, 1);
                                  }}
                                  disabled={viewGenerating}
                                >
                                  {g.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {(selectedService || loadingVoices || voices.length > 0) && (
                          <>
                            <div className="qgen-modal__voice-list-header">
                              <span className="qgen-modal__voice-list-title">
                                {i18n.t("Voices") || "Sesler"}
                              </span>
                            </div>
                            <div className="qgen-modal__voice-list">
                              {loadingVoices ? (
                                <div className="qgen-modal__empty">
                                  {i18n.t("Loading") || "Loading"}...
                                </div>
                              ) : voices.length === 0 ? (
                                <div className="qgen-modal__empty">
                                  {i18n.t("NoVoicesFound") || "No voices found"}
                                </div>
                              ) : (
                                <>
                                  {voices.map((voice) => {
                                    const hasSample = voice.sampleUrlsByLanguage
                                      ? Object.keys(voice.sampleUrlsByLanguage).length > 0
                                      : !!(
                                          voice.sampleUrl ||
                                          voice.audioSampleUrl ||
                                          voice.previewUrl
                                        );
                                    const isActive = selectedVoice?.id === voice.id;
                                    return (
                                      <div
                                        key={voice.id}
                                        className={`qgen-modal__voice-card${isActive ? " qgen-modal__voice-card--active" : ""}`}
                                        onClick={() => {
                                          setSelectedVoice(voice);
                                          // "Tümü" filtresinde provider boş; seçilen sesin gerçek sağlayıcısını set et.
                                          if (!selectedService) {
                                            const svc = voiceToService(voice);
                                            if (svc) setSelectedService(svc);
                                          }
                                          if (voice.language) {
                                            setSelectedLanguage(
                                              normalizeLanguageCode(voice.language)
                                            );
                                          }
                                          if (voice.gender) {
                                            setSelectedGender(normalizeGender(voice.gender));
                                          }
                                        }}
                                      >
                                        <button
                                          type="button"
                                          className="qgen-modal__voice-card-play"
                                          style={genderAvatarStyle(voice.gender)}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSamplePlay(voice);
                                          }}
                                          disabled={!hasSample}
                                        >
                                          {samplePlayingId === voice.id ? (
                                            <LuPause size={14} />
                                          ) : (
                                            <LuPlay size={14} />
                                          )}
                                        </button>
                                        <div className="qgen-modal__voice-card-body">
                                          <div className="qgen-modal__voice-card-name">
                                            {voice.name}
                                          </div>
                                          <div className="qgen-modal__voice-card-meta">
                                            {voice.gender && (
                                              <span>
                                                {normalizeGender(voice.gender) === "male"
                                                  ? i18n.t("Male") || "Erkek"
                                                  : i18n.t("Female") || "Kadın"}
                                              </span>
                                            )}
                                            {voice.servicename && <span>{voice.servicename}</span>}
                                          </div>
                                        </div>
                                        {isActive && (
                                          <span className="qgen-modal__voice-card-check">
                                            <LuCheck size={14} />
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {totalPages > 1 && (
                                    <div className="qgen-modal__voice-pagination">
                                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                                        (page) => (
                                          <button
                                            key={page}
                                            type="button"
                                            className={`qgen-modal__voice-pagination-btn${currentPage === page ? " qgen-modal__voice-pagination-btn--active" : ""}`}
                                            onClick={() => {
                                              setCurrentPage(page);
                                              loadVoices(
                                                selectedService,
                                                selectedLanguage,
                                                selectedGender,
                                                page
                                              );
                                            }}
                                          >
                                            {page}
                                          </button>
                                        )
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Soru tipi + adet — GÖSTERİLEN (viewed) sahnenin kendi kompozisyonu.
            Sadece o sahnenin toggle'ı açıkken düzenlenebilir. */}
                  <div className="qgen-modal__section-header">
                    <p className="qgen-modal__section-label">
                      {i18n.t("QuestionGenerator.QuestionType") || "Soru Tipi"}
                    </p>
                    {(() => {
                      // Üretim başlayınca counts silinir; o an generatingScenes[activeKey]
                      // adedini göster ki toplam üretim bitene kadar sabit kalsın.
                      const total = sceneTotal(activeKey) || generatingScenes[activeKey] || 0;
                      return (
                        <span
                          className="qgen-modal__qtype-total"
                          style={{ visibility: total > 0 ? "visible" : "hidden" }}
                        >
                          {total} {i18n.t("QuestionGenerator.QuestionCount") || "soru"}
                        </span>
                      );
                    })()}
                  </div>
                  <div
                    className={`qgen-modal__qtype-grid${
                      mainQtypeDisabled ? " qgen-modal__qtype-grid--disabled" : ""
                    }`}
                  >
                    {QUESTION_TYPE_DEFS.map((qt) => {
                      const label = i18n.t(qt.nameKey) || qt.fallback;
                      const count = sceneCounts(activeKey)[qt.value] || 0;
                      return (
                        <div
                          key={qt.value}
                          className={`qgen-modal__qtype-card${count > 0 ? " qgen-modal__qtype-card--on" : ""}`}
                        >
                          <div
                            className="qgen-modal__qtype-preview"
                            onClick={() =>
                              !mainQtypeDisabled && setTypeCount(activeKey, qt.value, count + 1)
                            }
                            title={label}
                          >
                            {qt.preview ? (
                              <img src={qt.preview} alt={label} />
                            ) : (
                              <span
                                className="material-icons"
                                style={{ fontSize: 40, color: "#94a3b8" }}
                              >
                                {qt.icon}
                              </span>
                            )}
                          </div>
                          <div className="qgen-modal__qtype-footer">
                            <span className="qgen-modal__qtype-label">{label}</span>
                            <div className="qgen-modal__qtype-stepper">
                              <button
                                type="button"
                                onClick={() => setTypeCount(activeKey, qt.value, count - 1)}
                                disabled={mainQtypeDisabled || count === 0}
                                aria-label="-"
                              >
                                −
                              </button>
                              <span>{count}</span>
                              <button
                                type="button"
                                onClick={() => setTypeCount(activeKey, qt.value, count + 1)}
                                disabled={mainQtypeDisabled}
                                aria-label="+"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {errorBySceneId[activeKey] && (
                    <p className="qgen-modal__error">{errorBySceneId[activeKey]}</p>
                  )}

                  {!isCombined && committedCount(activeKey) > 0 && (
                    <p className="qgen-modal__generated-note">
                      {i18n.t("QuestionGenerator.AddedForScene") || "Bu sahneye eklenen soru"}:{" "}
                      {committedCount(activeKey)}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      }
    </AppModal>
  );
};

export default QuestionGeneratorModal;
